import { notNullish } from '@fifteen/shared-lib';

import { mapCmsBlogDataToBlogHomeProps } from '@/lib/cms-mappers';
import { PAGE_SIZE } from '@/config/strapi-content';
import {
  mapCmsHeroDataToSeoMeta,
  mapCmsSeoDataToSeoMeta,
} from '@/lib/cms-mappers/common.mappers';

import type { FcwBlogHomeTemplateProps } from '@/components/templates/FcwBlogHomeTemplate.vue';

const homePopulateFields = ['hero.image', 'seo.image'];
const blogPostsPopulateFields = ['hero_image'];

/**
 * Returns blog single type + blog posts data fetched from CMS (Strapi)
 */
export async function useCmsBlogHome(): UseCmsResponse<
  FcwBlogHomeTemplateProps,
  'seoData'
> {
  const blogHome = useStrapi('blog', {
    queryParams: {
      populate: homePopulateFields.join(','),
    },
  });
  const blogPosts = useStrapi('blog-posts', {
    queryParams: {
      populate: blogPostsPopulateFields.join(','),
      sort: { publishedAt: 'desc' },
      pagination: {
        page: 0,
        pageSize: PAGE_SIZE,
      },
    },
  });

  const [
    {
      data: blogHomeData,
      error: blogHomeError,
      pending: blogHomePending,
      refresh: blogHomeRefresh,
    },
    {
      data: blogPostsData,
      error: blogPostsError,
      pending: blogPostsPending,
      refresh: blogPostsRefresh,
    },
  ] = await Promise.all([blogHome, blogPosts]);

  const errors = computed(() =>
    [blogHomeError.value, blogPostsError.value].filter(notNullish)
  );
  const pending = computed(
    () => blogHomePending.value || blogPostsPending.value
  );
  async function refresh(): Promise<void> {
    await Promise.all([blogHomeRefresh, blogPostsRefresh]);
  }

  const mappedData = computed(() =>
    mapCmsBlogDataToBlogHomeProps(
      blogHomeData.value?.data,
      blogPostsData.value?.data
    )
  );

  const seoData = computed(() =>
    blogHomeData.value?.data.attributes?.seo
      ? mapCmsSeoDataToSeoMeta(blogHomeData.value?.data.attributes?.seo)
      : mapCmsHeroDataToSeoMeta(blogHomeData.value?.data.attributes?.hero)
  );

  return { data: mappedData, seoData, errors, pending, refresh };
}
